import { JSX, useCallback, useRef, useState } from 'react'

import { Controller, FieldValues } from 'react-hook-form'

import {
  Button,
  Checkbox,
  type CLSize,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Group,
  type GroupProps,
  List,
} from '~/components/component-library'
import { CheckboxGroupInputProps } from '~/components/component-library/CheckboxGroupInput/types'
import { ListItemProps } from '~/components/component-library/List/types'
import { cn } from '~/utils/tailwind-utils'

const SELECT_ALL_MIN_OPTIONS = 3

const SelectAllSecondaryDisplay = <V extends string | number>({
  options,
  selectedValues,
  onToggle,
}: {
  options: CheckboxGroupInputProps<V>['options']
  selectedValues: V[]
  onToggle: (values: V[]) => void
}) => {
  const enabledValues = options
    .filter((option) => !option.disabled)
    .map((option) => option.value)
  // Disabled values are bystanders — bulk toggles preserve their current state.
  const preservedDisabledValues = selectedValues.filter(
    (v) => !enabledValues.includes(v),
  )
  const allSelected = enabledValues.every((v) => selectedValues.includes(v))
  const noneSelected = !enabledValues.some((v) => selectedValues.includes(v))

  return (
    <div className="flex gap-2">
      <Button
        variant="secondaryAction"
        isDisabled={allSelected}
        onClick={() => onToggle([...preservedDisabledValues, ...enabledValues])}
      >
        Select all
      </Button>
      <Button
        variant="secondaryAction"
        isDisabled={noneSelected}
        onClick={() => onToggle(preservedDisabledValues)}
      >
        Deselect all
      </Button>
    </div>
  )
}

const CheckboxGroupSmall = <V extends string | number>({
  options,
  collapsedOptions,
  selectedValues,
  onToggle,
  maxHeight,
}: {
  options: CheckboxGroupInputProps<V>['options']
  collapsedOptions?: CheckboxGroupInputProps<V>['collapsedOptions']
  selectedValues: V[]
  onToggle: (values: V[]) => void
  maxHeight?: string
}) => {
  const hiddenOptions = collapsedOptions?.options ?? []
  // Start expanded so an already-selected hidden option is never invisible.
  const [isExpanded, setIsExpanded] = useState(() =>
    hiddenOptions.some((option) => selectedValues.includes(option.value)),
  )
  const visibleOptions = isExpanded ? [...options, ...hiddenOptions] : options

  const enabledIndices = visibleOptions
    .map((option, index) => ({ option, index }))
    .filter(({ option }) => !option.disabled)
    .map(({ index }) => index)

  const firstEnabledIndex = enabledIndices[0] ?? 0
  const [highlightedIndex, setHighlightedIndex] = useState(firstEnabledIndex)

  const toggleValue = (optionValue: V) => {
    const newValue = selectedValues.includes(optionValue)
      ? selectedValues.filter((v) => v !== optionValue)
      : [...selectedValues, optionValue]
    onToggle(newValue)
  }

  const itemsRef = useRef<(HTMLDivElement | null)[]>([])

  const scrollToIndex = useCallback((index: number) => {
    itemsRef.current[index]?.scrollIntoView({ block: 'nearest' })
  }, [])

  const getNextEnabledIndex = (
    current: number,
    direction: 'next' | 'prev',
  ): number => {
    if (enabledIndices.length === 0) return current
    const position = enabledIndices.indexOf(current)
    if (position === -1) return enabledIndices[0]
    const nextPosition =
      direction === 'next'
        ? (position + 1) % enabledIndices.length
        : (position - 1 + enabledIndices.length) % enabledIndices.length
    return enabledIndices[nextPosition]
  }

  return (
    <div
      role="group"
      tabIndex={0}
      onKeyDown={(e) => {
        // Don't hijack keys from a focused child button (the collapsible trigger).
        if (e.target instanceof HTMLElement && e.target.closest('button')) {
          return
        }
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault()
          const nextIndex = getNextEnabledIndex(
            highlightedIndex,
            e.key === 'ArrowDown' ? 'next' : 'prev',
          )
          setHighlightedIndex(nextIndex)
          scrollToIndex(nextIndex)
        } else if (
          e.key === ' ' ||
          (e.key === 'Enter' && !e.metaKey && !e.ctrlKey) ||
          e.key === 'Backspace'
        ) {
          e.preventDefault()
          const target = visibleOptions[highlightedIndex]
          if (target && !target.disabled) toggleValue(target.value)
        }
      }}
      onBlur={() => setHighlightedIndex(firstEnabledIndex)}
      className="group/checkboxgroup -mx-2 -mt-1 flex w-[calc(100%+1rem)] flex-col rounded-lg py-0.5 outline-none ring-inset focus-visible:ring-2 focus-visible:ring-blue-500"
    >
      <div
        tabIndex={-1}
        className={cn('flex flex-col overflow-y-auto outline-none', maxHeight)}
      >
        {options.map((option, index) => (
          <CheckboxGroupSmallOption
            key={`checkbox-option-${index}`}
            option={option}
            isSelected={selectedValues.includes(option.value)}
            isHighlighted={highlightedIndex === index}
            onToggle={toggleValue}
            itemRef={(el) => {
              itemsRef.current[index] = el
            }}
          />
        ))}
        {collapsedOptions && hiddenOptions.length > 0 && (
          <Collapsible
            open={isExpanded}
            onOpenChange={(open) => {
              setIsExpanded(open)
              // The highlight can't point at a row that just collapsed away.
              if (!open && highlightedIndex >= options.length) {
                setHighlightedIndex(firstEnabledIndex)
              }
            }}
          >
            <CollapsibleTrigger className="rounded-lg px-2 py-1.5 text-xs text-secondary ring-inset hover:text-primary focus-visible:ring-2 focus-visible:ring-blue-500">
              {isExpanded
                ? collapsedOptions.hideLabel
                : collapsedOptions.showLabel}
            </CollapsibleTrigger>
            <CollapsibleContent className="flex flex-col">
              {hiddenOptions.map((option, hiddenIndex) => {
                const index = options.length + hiddenIndex
                return (
                  <CheckboxGroupSmallOption
                    key={`checkbox-option-${index}`}
                    option={option}
                    isSelected={selectedValues.includes(option.value)}
                    isHighlighted={highlightedIndex === index}
                    onToggle={toggleValue}
                    itemRef={(el) => {
                      itemsRef.current[index] = el
                    }}
                  />
                )
              })}
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>
    </div>
  )
}

const CheckboxGroupSmallOption = <V extends string | number>({
  option,
  isSelected,
  isHighlighted,
  onToggle,
  itemRef,
}: {
  option: CheckboxGroupInputProps<V>['options'][number]
  isSelected: boolean
  isHighlighted: boolean
  onToggle: (value: V) => void
  itemRef: (el: HTMLDivElement | null) => void
}) => {
  const isDisabled = !!option.disabled
  return (
    <div
      ref={itemRef}
      role="checkbox"
      aria-checked={isSelected}
      aria-disabled={isDisabled}
      onClick={() => {
        if (!isDisabled) onToggle(option.value)
      }}
      className={cn(
        'group/item relative flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm',
        {
          'cursor-pointer': !isDisabled,
          'cursor-not-allowed opacity-50': isDisabled,
          'group-focus-visible/checkboxgroup:after:pointer-events-none group-focus-visible/checkboxgroup:after:absolute group-focus-visible/checkboxgroup:after:inset-x-1.5 group-focus-visible/checkboxgroup:after:inset-y-1 group-focus-visible/checkboxgroup:after:rounded group-focus-visible/checkboxgroup:after:ring-2 group-focus-visible/checkboxgroup:after:ring-blue-500':
            isHighlighted,
        },
      )}
    >
      <Checkbox isChecked={isSelected} isDisabled={isDisabled} />
      <div className="min-w-0 text-sm text-primary">
        {option.display ? option.display(isSelected) : option.label}
      </div>
    </div>
  )
}

const CheckboxGroupContent = <V extends string | number>({
  options,
  collapsedOptions,
  selectedValues,
  onToggle,
  label,
  description,
  footer,
  tooltip,
  secondaryDisplay,
  size = 'base',
  cta,
  maxHeight,
}: Pick<
  GroupProps,
  'label' | 'description' | 'footer' | 'tooltip' | 'secondaryDisplay'
> & {
  options: CheckboxGroupInputProps<V>['options']
  collapsedOptions?: CheckboxGroupInputProps<V>['collapsedOptions']
  selectedValues: V[]
  onToggle: (values: V[]) => void
  size?: CLSize
  cta?: CheckboxGroupInputProps<V>['cta']
  maxHeight?: string
}) => {
  const toggleValue = (optionValue: V) => {
    const newValue = selectedValues.includes(optionValue)
      ? selectedValues.filter((v) => v !== optionValue)
      : [...selectedValues, optionValue]
    onToggle(newValue)
  }

  // Auto-render a Select all / Deselect all affordance once there are enough
  // options to make manual toggling tedious. Base size only — the small
  // variant is used in tight popovers where the extra row is noise.
  // A caller-provided `secondaryDisplay` always wins.
  const resolvedSecondaryDisplay =
    secondaryDisplay ??
    (size === 'base' && options.length >= SELECT_ALL_MIN_OPTIONS ? (
      <SelectAllSecondaryDisplay
        options={options}
        selectedValues={selectedValues}
        onToggle={onToggle}
      />
    ) : undefined)

  if (size === 'small') {
    return (
      <Group
        label={label}
        footer={footer}
        description={description}
        tooltip={tooltip}
        secondaryDisplay={resolvedSecondaryDisplay}
      >
        <CheckboxGroupSmall
          options={options}
          collapsedOptions={collapsedOptions}
          selectedValues={selectedValues}
          onToggle={onToggle}
          maxHeight={maxHeight}
        />
      </Group>
    )
  }

  const listItems: ListItemProps[] = options.map((option, index) => {
    const isSelected = selectedValues.includes(option.value)
    const isDisabled = !!option.disabled
    return {
      key: `checkbox-option-${index}`,
      isDisabled,
      image: <Checkbox isChecked={isSelected} isDisabled={isDisabled} />,
      content: option.display
        ? option.display(isSelected)
        : (option.label ?? ''),
      description: option.description,
      meta: option.meta,
      onClick: () => toggleValue(option.value),
    }
  })

  return (
    <List
      items={listItems}
      cta={cta}
      label={label}
      footer={footer}
      description={description}
      tooltip={tooltip}
      secondaryDisplay={resolvedSecondaryDisplay}
    />
  )
}

export const CheckboxGroupInput = <
  V extends string | number = string,
  FormType extends FieldValues = FieldValues,
>({
  control,
  inputName,
  options,
  collapsedOptions,
  label,
  description,
  footer,
  tooltip,
  secondaryDisplay,
  size = 'base',
  cta,
  maxHeight,
  ...rest
}: CheckboxGroupInputProps<V, FormType> & {
  maxHeight?: string
}): JSX.Element => {
  // Controlled mode (no react-hook-form)
  if (!control || !inputName) {
    const { value, onChange } = rest as {
      value: V[]
      onChange: (v: V[]) => void
    }
    return (
      <CheckboxGroupContent<V>
        options={options}
        collapsedOptions={collapsedOptions}
        selectedValues={value ?? []}
        onToggle={onChange}
        label={label}
        description={description}
        footer={footer}
        tooltip={tooltip}
        secondaryDisplay={secondaryDisplay}
        size={size}
        cta={cta}
        maxHeight={maxHeight}
      />
    )
  }

  return (
    <Controller
      control={control}
      name={inputName}
      render={({ field }) => (
        <CheckboxGroupContent<V>
          options={options}
          collapsedOptions={collapsedOptions}
          selectedValues={(field.value as V[]) ?? []}
          onToggle={field.onChange}
          label={label}
          description={description}
          footer={footer}
          tooltip={tooltip}
          secondaryDisplay={secondaryDisplay}
          size={size}
          cta={cta}
          maxHeight={maxHeight}
        />
      )}
    />
  )
}

CheckboxGroupInput.displayName = 'CheckboxGroupInput'

export type { CheckboxGroupInputProps } from '~/components/component-library/CheckboxGroupInput/types'
